import { Component, OnInit, inject } from '@angular/core';
import {
	FacebookConnection,
	NeedsPageAccessAccount
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { FacebookConnectionsService } from '@brand-lolly/app/services/meta-integrations/facebook-connections.service';
import { extractMetaErrorMessage } from '@brand-lolly/app/shared/utils/meta-api-error.utils';
import {
	LO_DIALOG_DATA,
	LoAvatar,
	LoButton,
	LoDialog,
	LoDialogRef,
	LoIcon,
	LoStatus,
	LoToastService
} from 'lo-design-system';
import { MetaNeedsPageAccessComponent } from './meta-needs-page-access.component';
import { MetaPermRowComponent } from './meta-perm-row.component';

export interface MetaFbConnectionModalData {
	connection: FacebookConnection;
	igCount: number;
}

export interface MetaFbConnectionModalResult {
	refreshed?: boolean;
	removed?: boolean;
}

interface KnownScope {
	code: string;
	description: string;
	disabled?: boolean;
}

const KNOWN_SCOPES: KnownScope[] = [
	{
		code: 'ads_management',
		description: 'Manage ads across linked accounts'
	},
	{
		code: 'instagram_manage_insights',
		description: 'Read analytics & engagement data'
	},
	{
		code: 'instagram_content_publish',
		description: 'Publish posts and stories'
	},
	{ code: 'pages_show_list', description: 'List Facebook Pages' },
	{
		code: 'pages_read_engagement',
		description: 'Read Page mentions and engagement'
	},
	{
		code: 'instagram_creator_marketplace_discovery',
		description: 'Creator Marketplace access'
	},
	{
		code: 'instagram_branded_content_brand',
		description: 'Partnership Ads (pair-level activation)'
	},
	{
		code: 'business_management',
		description: 'Manage Business Manager assets',
		disabled: true
	}
];

@Component({
	standalone: true,
	selector: 'meta-fb-connection-modal',
	imports: [
		LoAvatar,
		LoButton,
		LoDialog,
		LoIcon,
		LoStatus,
		MetaPermRowComponent,
		MetaNeedsPageAccessComponent
	],
	template: `
		<lo-dialog
			[heading]="data.connection.fb_user_name || 'Facebook user'"
			(dismissed)="close()"
		>
			<div class="content">
				<div class="lead">
					<lo-avatar
						[name]="data.connection.fb_user_name || 'Facebook user'"
						[src]="data.connection.fb_user_picture_url || ''"
					></lo-avatar>
					<span class="lo-type-text-sm">
						Facebook connection · fb_id {{ data.connection.fb_user_id }}
					</span>
				</div>

				<section class="section">
					<h3 class="lo-type-text-md-semibold">Connection</h3>
					<dl class="info lo-type-text-md">
						<dt>Status</dt>
						<dd><lo-status dot tone="success">Active</lo-status></dd>
						<dt>Token expires</dt>
						<dd>
							<span>{{ expiresLabel }}</span>
							<button
								lo-button
								variant="link"
								size="sm"
								type="button"
								[loading]="refreshLoading"
								(click)="refreshToken()"
							>
								<lo-icon name="refresh" />
								Refresh token
							</button>
						</dd>
						<dt>Business</dt>
						<dd>{{ businessLabel }}</dd>
						<dt>Linked accounts</dt>
						<dd>
							{{ adCount }} ad accounts · {{ data.igCount }} Instagram
							accounts
						</dd>
					</dl>
					<div>
						<button
							lo-button
							variant="secondary"
							size="sm"
							type="button"
							[loading]="discoveryLoading"
							(click)="runDiscovery()"
						>
							Run discovery
						</button>
					</div>
				</section>

				@if (needsPageAccess.length || showAccountsPointer) {
					<meta-needs-page-access
						[accounts]="needsPageAccess"
						[showPointer]="showAccountsPointer"
					></meta-needs-page-access>
				}

				<section class="section">
					<h3 class="lo-type-text-md-semibold">Permissions</h3>
					<p class="hint lo-type-text-sm">
						OAuth scopes granted when you connected Facebook. Each
						unlocks specific capabilities.
					</p>
					<div>
						@for (scope of scopes; track scope.code) {
							<meta-perm-row
								[code]="scope.code"
								[description]="scope.description"
								[granted]="!scope.disabled && isGranted(scope.code)"
								[disabled]="!!scope.disabled"
							></meta-perm-row>
						}
					</div>
				</section>
			</div>
			<div loDialogActions>
				<button
					lo-button
					variant="secondary"
					type="button"
					(click)="disconnect()"
				>
					Disconnect
				</button>
				<button lo-button type="button" (click)="close()">Done</button>
			</div>
		</lo-dialog>
	`,
	styles: [
		`
			.content {
				display: flex;
				flex-direction: column;
				gap: var(--lo-space-gap-xl);

				.lead {
					display: flex;
					align-items: center;
					gap: var(--lo-space-gap-md);
				}

				.section {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-md);

					h3,
					p {
						margin: 0;
					}

					.hint {
						color: var(--lo-color-text-secondary);
					}

					.info {
						display: grid;
						grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
						gap: var(--lo-space-gap-md) var(--lo-space-gap-lg);
						margin: 0;

						dt {
							color: var(--lo-color-text-secondary);
						}

						dd {
							display: flex;
							align-items: center;
							flex-wrap: wrap;
							gap: var(--lo-space-gap-md);
							margin: 0;
							overflow-wrap: anywhere;
						}
					}
				}
			}
		`
	]
})
export class MetaFbConnectionModalComponent implements OnInit {
	protected readonly data = inject<MetaFbConnectionModalData>(LO_DIALOG_DATA);
	private readonly dialogRef =
		inject<
			LoDialogRef<
				MetaFbConnectionModalComponent,
				MetaFbConnectionModalResult
			>
		>(LoDialogRef);
	private readonly toasts = inject(LoToastService);
	scopes: KnownScope[] = KNOWN_SCOPES;
	refreshLoading = false;
	discoveryLoading = false;
	needsPageAccess: NeedsPageAccessAccount[] = [];
	/** The zero-accounts state: say where an account Meta did not show lives. */
	showAccountsPointer = false;
	/** A walk kept the modal open to show its result; the list still needs a reload. */
	discoveryRan = false;

	constructor(private service: FacebookConnectionsService) {}

	ngOnInit(): void {
		this.needsPageAccess = this.data.connection.needs_page_access ?? [];
		this.showAccountsPointer = this.data.igCount === 0;
	}

	get expiresLabel(): string {
		const expires = this.data.connection.expires_at;
		if (!expires) return '—';
		const date = new Date(expires);
		const days = Math.round((date.getTime() - Date.now()) / 86400000);
		return `${date.toLocaleDateString()} (in ${days} days)`;
	}

	get businessLabel(): string {
		const names = this.data.connection.business_ids
			?.map(b => b.name)
			.filter(Boolean);
		return names && names.length ? names.join(', ') : '—';
	}

	get adCount(): number {
		return this.data.connection.ad_account_ids?.length ?? 0;
	}

	isGranted(scope: string): boolean {
		return this.data.connection.granted_scopes?.includes(scope) ?? false;
	}

	refreshToken(): void {
		this.refreshLoading = true;
		this.service.refreshToken(this.data.connection.id).subscribe({
			next: () => {
				this.refreshLoading = false;
				this.toasts.success('Token refreshed');
				this.dialogRef.close({ refreshed: true });
			},
			error: error => {
				this.refreshLoading = false;
				this.toasts.error(
					extractMetaErrorMessage(error, 'Failed to refresh token')
				);
			}
		});
	}

	runDiscovery(): void {
		this.discoveryLoading = true;
		this.service.refresh(this.data.connection.id).subscribe({
			next: response => {
				this.discoveryLoading = false;
				this.toasts.success('Discovery complete');
				const needsPageAccess =
					response.connection?.needs_page_access ?? [];
				if (!needsPageAccess.length) {
					this.dialogRef.close({ refreshed: true });
					return;
				}
				// Stays open: these accounts were not connected, and closing
				// would hide the only place that says what to do about them.
				this.needsPageAccess = needsPageAccess;
				this.discoveryRan = true;
			},
			error: error => {
				this.discoveryLoading = false;
				this.toasts.error(
					extractMetaErrorMessage(error, 'Discovery failed')
				);
			}
		});
	}

	disconnect(): void {
		this.service.remove(this.data.connection.id).subscribe({
			next: () => {
				this.toasts.success('Connection removed');
				this.dialogRef.close({ removed: true });
			},
			error: error =>
				this.toasts.error(
					extractMetaErrorMessage(error, 'Failed to remove')
				)
		});
	}

	close(): void {
		this.dialogRef.close(
			this.discoveryRan ? { refreshed: true } : undefined
		);
	}
}
